<template>
  <div class="cvhp-root" data-testid="checkpoint-version-history-root">
    <div
      class="cvhp__overlay"
      data-testid="checkpoint-version-history-overlay"
      @click="emit('close')"
    />
    <aside
      class="cvhp"
      role="complementary"
      aria-label="Version History"
      data-testid="checkpoint-version-history-panel"
    >
      <header class="cvhp__header">
        <h2 class="cvhp__title">Version History</h2>
        <button
          type="button"
          class="cvhp__close"
          aria-label="Close"
          data-testid="checkpoint-version-history-close"
          @click="emit('close')"
        >
          <span aria-hidden="true">×</span>
        </button>
      </header>

      <div class="cvhp__body" data-testid="checkpoint-version-history-body">
        <div v-if="loading" class="cvhp__state" data-testid="checkpoint-version-history-loading">
          Loading version history…
        </div>

        <div
          v-else-if="isEmpty"
          class="cvhp__state"
          data-testid="checkpoint-version-history-empty"
        >
          No version history yet.
        </div>

        <div
          v-else-if="error"
          class="cvhp__state cvhp__state--error"
          data-testid="checkpoint-version-history-error"
        >
          {{ error }}
        </div>

        <div
          v-else
          class="cvhp__groups"
          data-testid="checkpoint-version-history-groups"
        >
          <section
            v-for="group in dateGroups"
            :key="group.key"
            class="cvhp__group"
          >
            <div class="cvhp__group-heading">
              <p class="cvhp__group-label">{{ group.label }}</p>
              <div class="cvhp__group-divider" aria-hidden="true" />
            </div>

            <button
              v-for="row in group.versions"
              :key="row.id"
              type="button"
              class="cvhp__row"
              :class="{ 'cvhp__row--selected': row.isSelected }"
              :aria-pressed="row.isSelected ? 'true' : 'false'"
              :aria-label="row.ariaLabel"
              data-testid="checkpoint-version-history-row"
              @click="onSelect(row.source)"
            >
              <ExAvatar
                class="cvhp__avatar"
                size="small"
                :picture="row.publisherAvatar"
                :firstName="row.publisherFirstName"
                :lastName="row.publisherLastName"
              />

              <div class="cvhp__row-content">
                <div class="cvhp__pills">
                  <span class="cvhp__pill cvhp__pill--version">{{ row.versionLabel }}</span>
                  <span
                    v-if="row.statusLabel"
                    class="cvhp__pill"
                    :class="row.statusClass"
                  >
                    {{ row.statusLabel }}
                  </span>
                </div>

                <div class="cvhp__meta">
                  <span class="cvhp__publisher">{{ row.publisherName }}</span>
                  <span class="cvhp__dot" aria-hidden="true">•</span>
                  <span class="cvhp__time">{{ row.timeLabel }}</span>
                </div>
              </div>
            </button>
          </section>
        </div>
      </div>
    </aside>
  </div>
</template>

<script setup>
import { computed } from "vue";
import ExAvatar from "@components/common/ExAvatar.vue";

const props = defineProps({
  /**
   * Display-ready version rows.
   * @type {{
   *   id: number|string,
   *   versionNumber: number,
   *   publishedAt: string,
   *   publisherName?: string,
   *   publisherAvatar?: string|null,
   *   publisherFirstName?: string,
   *   publisherLastName?: string,
   *   isCurrent?: boolean,
   *   isRetired?: boolean,
   * }[]}
   */
  versions: {
    type: Array,
    default: () => [],
  },
  /** Highlighted / active row. Defaults to the current version when omitted. */
  selectedVersionId: {
    type: [Number, String],
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(["close", "select"]);

const isEmpty = computed(() => !props.loading && !props.error && props.versions.length === 0);

const effectiveSelectedId = computed(() => {
  if (props.selectedVersionId != null) return props.selectedVersionId;
  const current = props.versions.find((v) => v.isCurrent);
  return current ? current.id : null;
});

const dateGroups = computed(() => {
  const groups = [];
  const indexByKey = new Map();

  props.versions.forEach((version) => {
    const publishedAt = version.publishedAt;
    const key = groupKeyFor(publishedAt);
    const label = formatGroupLabel(publishedAt);
    const versionLabel = `V${version.versionNumber}`;
    const statusLabel = version.isRetired
      ? "Retired"
      : version.isCurrent
        ? "Current"
        : null;
    const statusClass = version.isRetired
      ? "cvhp__pill--retired"
      : version.isCurrent
        ? "cvhp__pill--current"
        : "";
    const publisherName = version.publisherName || "Unknown";
    const timeLabel = formatTime(publishedAt);
    const publisherFirstName =
      version.publisherFirstName || publisherName.trim().split(/\s+/)[0] || "U";
    const nameParts = publisherName.trim().split(/\s+/);
    const publisherLastName =
      version.publisherLastName ||
      (nameParts.length > 1 ? nameParts[nameParts.length - 1] : "N");
    const ariaParts = [versionLabel];
    if (statusLabel) ariaParts.push(statusLabel);
    if (publisherName) ariaParts.push(publisherName);
    if (timeLabel) ariaParts.push(timeLabel);

    const row = {
      id: version.id,
      source: version,
      versionLabel,
      statusLabel,
      statusClass,
      publisherName,
      publisherAvatar: version.publisherAvatar || null,
      publisherFirstName,
      publisherLastName,
      timeLabel,
      isSelected:
        effectiveSelectedId.value != null && version.id === effectiveSelectedId.value,
      ariaLabel: ariaParts.join(", "),
    };

    if (!indexByKey.has(key)) {
      indexByKey.set(key, groups.length);
      groups.push({ key, label, versions: [] });
    }

    groups[indexByKey.get(key)].versions.push(row);
  });

  return groups;
});

function onSelect(version) {
  emit("select", version);
}

function toDate(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function isSameDay(a, b) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function groupKeyFor(publishedAt) {
  const date = toDate(publishedAt);
  if (!date) return "unknown";
  if (isSameDay(date, new Date())) return "today";
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function formatGroupLabel(publishedAt) {
  const date = toDate(publishedAt);
  if (!date) return "Unknown";
  if (isSameDay(date, new Date())) return "Today";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatTime(publishedAt) {
  const date = toDate(publishedAt);
  if (!date) return "";
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}
</script>

<style scoped>
.cvhp-root {
  display: contents;
}

.cvhp__overlay {
  position: fixed;
  inset: 0;
  z-index: 200;
  background: rgba(16, 24, 40, 0.4);
}

.cvhp {
  position: fixed;
  top: 0;
  right: 0;
  z-index: 201;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 400px;
  max-width: 100vw;
  height: 100vh;
  overflow: hidden;
  background: #ffffff;
  border-left: 1px solid #d0d5dd;
  box-shadow: -4px 0 24px rgba(16, 24, 40, 0.12);
}

.cvhp__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  width: 100%;
  padding: 20px 16px 20px 24px;
  border-bottom: 1px solid #d0d5dd;
}

.cvhp__title {
  margin: 0;
  color: #101828;
  font-size: 18px;
  font-weight: 700;
  line-height: normal;
}

.cvhp__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  padding: 0;
  color: #344054;
  font-size: 16px;
  font-weight: 600;
  line-height: 1;
  background: #f9fafb;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  cursor: pointer;
}

.cvhp__close:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.cvhp__body {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  width: 100%;
  min-height: 0;
  padding: 20px 24px 24px;
  overflow-x: hidden;
  overflow-y: auto;
}

.cvhp__state {
  padding: 12px 8px;
  color: #475367;
  font-size: 14px;
  line-height: 1.4;
}

.cvhp__state--error {
  color: #b42318;
}

.cvhp__groups {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
}

.cvhp__group {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.cvhp__group-heading {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  padding-bottom: 8px;
}

.cvhp__group-label {
  margin: 0;
  color: #475367;
  font-size: 14px;
  font-weight: 600;
  line-height: normal;
}

.cvhp__group-divider {
  width: 100%;
  height: 1px;
  background: #eaecf0;
}

.cvhp__row {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  width: 100%;
  padding: 12px 8px;
  text-align: left;
  background: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
}

.cvhp__row--selected {
  background: #f4f5ff;
}

.cvhp__row:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.cvhp__avatar {
  flex-shrink: 0;
}

.cvhp__row-content {
  display: flex;
  flex: 1 0 0;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
  min-width: 0;
}

.cvhp__pills {
  display: flex;
  gap: 6px;
  align-items: center;
}

.cvhp__pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  white-space: nowrap;
  border-radius: 9999px;
}

.cvhp__pill--version {
  color: #344054;
  background: #f2f4f7;
}

.cvhp__pill--current {
  color: #7f56d9;
  background: #ffffff;
  border: 1px solid #7f56d9;
}

.cvhp__pill--retired {
  color: #59616b;
  background: #ffffff;
  border: 1px solid #59616b;
}

.cvhp__meta {
  display: flex;
  gap: 6px;
  align-items: center;
  min-width: 0;
  font-size: 14px;
  line-height: normal;
  white-space: nowrap;
}

.cvhp__publisher {
  overflow: hidden;
  color: #344054;
  font-weight: 500;
  text-overflow: ellipsis;
}

.cvhp__dot,
.cvhp__time {
  color: #475367;
  font-weight: 400;
}
</style>
